// VIDE CONSTITUICAO_REGRAS.SH sec.2.2 | Art.188-A (Snapshot Bling em tempo real)
// ============================================================================
// Hook: useRectaBlingSnapshot
// Carrega snapshot Bling consolidado (gerado por
// scripts/python_tools/recta_scripta/gera_recta_bling_index.py).
//
// FASE 2 (auditoria 10/09/2026): antes vivia em /public/data/recta_bling_snapshot.json,
// servido estaticamente — expunha contas a pagar/receber, SALDOS bancários, boletos e
// contatos SEM login no site publicado. Agora vem do Firestore
// (artifacts/portalrda-f312d/public/data/private_datasets/recta_bling_snapshot),
// admin-only pela regra K do firestore.rules.
//
// Fluxo de atualização:
//   1. Operador roda `python -m recta_bling.extrai_snapshot` (auth Bling)
//   2. `python scripts\python_tools\recta_scripta\gera_recta_bling_index.py` grava no
//      Firestore (via Admin SDK) — NÃO mais em public/data/
//   3. Este hook lê do Firestore e cacheia
//   4. Botão "atualizar do servidor" chama POST /bling/refresh-snapshot (backend, auth)
// ============================================================================
import { useState, useEffect, useCallback } from 'react';
import { auth } from '../../shared/api/firebase/config';
import { loadPrivateDataset, clearPrivateDatasetCache, DATASET } from '../../shared/api/privateDatasets';

const API_BASE = (
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_RDA_API_BASE_URL) ||
  'https://rda-backend-thuq.onrender.com'
).replace(/\/+$/, '');

async function _getAuthHeaders() {
  try {
    const token = await auth.currentUser?.getIdToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export function clearRectaBlingSnapshotCache() {
  clearPrivateDatasetCache(DATASET.BLING_SNAPSHOT);
}

export function useRectaBlingSnapshot() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rev, setRev] = useState(0);

  const refresh = useCallback(() => {
    clearRectaBlingSnapshotCache();
    setData(null);
    setLoading(true);
    setError(null);
    setRev((r) => r + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    loadPrivateDataset(DATASET.BLING_SNAPSHOT, { force: rev > 0 })
      .then((json) => {
        if (cancelled) return;
        setData(json);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [rev]);

  const refreshFromServer = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await _getAuthHeaders();
      const res = await fetch(`${API_BASE}/bling/refresh-snapshot`, {
        method: 'POST',
        headers,
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail?.detail || `HTTP ${res.status}`);
      }
      const json = await res.json();
      clearRectaBlingSnapshotCache();
      setData(json);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, refresh, refreshFromServer };
}

// Helpers
export function getKpis(snapshot) {
  return snapshot?.kpis || null;
}

export function getContasReceber(snapshot) {
  return (snapshot?.contas_receber?.items || []);
}

export function getContasPagar(snapshot) {
  return (snapshot?.contas_pagar?.items || []);
}

export function getPropostasAbertas(snapshot) {
  const items = snapshot?.propostas_comerciais?.items || [];
  return items.filter((p) => {
    const sit = p.situacao;
    const id = (typeof sit === 'object' && sit) ? (sit.id || sit.valor) : sit;
    // 2 = ganha, 3 = perdida — filtrar só abertas
    return String(id) !== '2' && String(id) !== '3';
  });
}

export function getPedidos(snapshot) {
  return (snapshot?.pedidos_vendas?.items || []);
}

export function getContatosBling(snapshot) {
  return (snapshot?.contatos?.items || []);
}

export function getPortadores(snapshot) {
  return (snapshot?.kpis?.portadores || []);
}

export function getSaldoReservaCDB(snapshot) {
  return snapshot?.kpis?.saldo_reserva_cdb ?? 0;
}

export function getSaldoTotalPortadores(snapshot) {
  return snapshot?.kpis?.saldo_total_portadores ?? 0;
}
